/**
 * Every term the query must hit, lowercased. Splitting on whitespace makes
 * "delta renewal" match a workspace titled "Renewal review — Delta Freight",
 * which a single substring test would miss.
 */
export const queryTerms = (query: string) => query.toLowerCase().split(/\s+/).filter(Boolean);

/**
 * Whether every term starts a word somewhere in `haystack`. Nullable fields are
 * accepted so callers can pass optional descriptions and keywords straight in
 * without filtering them first.
 *
 * Anchored to word starts rather than matched anywhere: a plain substring test
 * makes "voice" hit the Subscription page, whose keywords contain "invoice".
 * Prefixes still match, so "ren" finds "Renewal review".
 */
export const matchesTerms = (terms: string[], ...haystack: (string | null | undefined)[]) => {
  if (terms.length === 0) {
    return false;
  }

  const words = haystack
    .filter((part): part is string => !!part)
    .join(' ')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

  return terms.every((term) => words.some((word) => word.startsWith(term)));
};

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Timestamps arrive as Date from the API's scalar mapping, string from tests. */
export type Timestamp = Date | string | null | undefined;

/**
 * Compact age for the right-hand column of a result row, in the shape the
 * palette has room for: "3d ago", never "3 days ago". Returns undefined for a
 * missing or unparseable timestamp so the column simply stays empty.
 */
export const relativeDate = (value: Timestamp, now = Date.now()) => {
  if (!value) {
    return undefined;
  }

  const elapsed = now - new Date(value).getTime();
  if (Number.isNaN(elapsed)) {
    return undefined;
  }

  if (elapsed < HOUR) {
    return `${Math.max(1, Math.floor(elapsed / MINUTE))}m ago`;
  }
  if (elapsed < DAY) {
    return `${Math.floor(elapsed / HOUR)}h ago`;
  }
  if (elapsed < 7 * DAY) {
    return `${Math.floor(elapsed / DAY)}d ago`;
  }
  if (elapsed < 30 * DAY) {
    return `${Math.floor(elapsed / (7 * DAY))}w ago`;
  }
  if (elapsed < 365 * DAY) {
    return `${Math.floor(elapsed / (30 * DAY))}mo ago`;
  }
  return `${Math.floor(elapsed / (365 * DAY))}y ago`;
};

/** Most recent first, falling back to creation time for rows never updated. */
export const byUpdatedAtDesc = <T extends { updatedAt?: Timestamp; createdAt?: Timestamp }>(
  a: T,
  b: T
) => timestampOf(b) - timestampOf(a);

const timestampOf = (row: { updatedAt?: Timestamp; createdAt?: Timestamp }) =>
  new Date(row.updatedAt ?? row.createdAt ?? 0).getTime();
